import { Map } from "immutable";
import { AgentJobDto } from "types/api/agentJobs";
import { abortReason, isAbortError, isTransientError, sleep } from "./polling";

export const POLL_INTERVAL_MS = 1_000;
// Client-owned budget: the backend never fails stuck jobs, so a job can stay
// `running` indefinitely. "Keep waiting" simply starts another budget.
export const POLL_TIMEOUT_MS = 60_000;

// Any api group whose `get(id, options)` returns the Immutable-wrapped job
// response; the options carry the poll's abort signal and remaining budget.
export type JobsApi = {
  get: (
    id: number,
    options?: { signal?: AbortSignal; timeout?: number },
  ) => Promise<unknown>;
};

export type PollOutcome<T extends AgentJobDto, S extends string> =
  | { kind: S; job: T }
  | { kind: "timeout" };

export type PollOptions<S extends string> = {
  jobsApi: JobsApi;
  terminalStatuses: readonly S[];
  signal?: AbortSignal;
  intervalMs?: number;
  timeoutMs?: number;
  now?: () => number;
};

// client.js wraps every success as fromJS({ headers, data }).
export const readAgentJob = <T extends AgentJobDto>(response: unknown): T =>
  (response as Map<string, any>).get("data").toJS();

// Re-exported for the panels and tests, which classify the poll rejection.
export { isAbortError };

// Polls until the job reaches one of `terminalStatuses`. Any other status
// (pending, running, or one the backend adds later) keeps polling until the
// deadline. Rejects with an AbortError when the signal fires.
export const pollAgentJob = async <T extends AgentJobDto, S extends string>(
  jobId: number,
  {
    jobsApi,
    terminalStatuses,
    signal,
    intervalMs = POLL_INTERVAL_MS,
    timeoutMs = POLL_TIMEOUT_MS,
    now = Date.now,
  }: PollOptions<S>,
): Promise<PollOutcome<T, S>> => {
  const deadline = now() + timeoutMs;
  const terminal: readonly string[] = terminalStatuses;

  while (now() < deadline) {
    await sleep(intervalMs, signal);
    try {
      // The request honours the signal and the remaining budget (0 would mean
      // no timeout to axios), so a stalled GET neither outlives the panel nor
      // blocks the deadline.
      const timeout = Math.max(1, deadline - now());
      const job = readAgentJob<T>(
        await jobsApi.get(jobId, { signal, timeout }),
      );
      if (signal?.aborted) throw abortReason(signal);
      if (terminal.includes(job.status)) {
        return { kind: job.status as S, job };
      }
    } catch (error) {
      // client.js reports axios' cancellation as a plain network error.
      if (signal?.aborted) throw abortReason(signal);
      if (!isTransientError(error)) throw error;
    }
  }

  return { kind: "timeout" };
};
